import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { CustomTypeOptions, Namespace } from 'i18next';
import { useTranslation } from 'react-i18next';
import { showToast } from '@unmind/design-system';

import { logException } from 'App/logging';
import { tracking } from 'App/Tracking';
import {
  cancelEventRequestMutation,
  cancelEventRequestMutationVariables,
} from 'Services/BeGateway/Talk/__generated__/cancelEventRequestMutation';
import {
  CANCEL_EVENT_REQUEST_MUTATION,
  TALK_EVENT_REQUESTS_QUERY,
} from 'Services/BeGateway/Talk/talk.services';
import { EventRequestCancelInput } from 'Services/BeGateway/__generated__/beGatewayTypes';
import Modal from 'Shared/Modal';
import Portal from 'Shared/Portal';
import { BEGatewayQueryContext } from 'utils/apollo';
import { Container, SubmitButton, MultiChoice } from './styles';

export type RequestCancellationReasonType =
  CustomTypeOptions['resources']['book']['sessionDetails']['cancel']['request']['reason'];

export interface Props {
  eventRequestID: string;
  open: boolean;
  onClose(): void;
  onCancelEventSuccess?(): void;
}

export function CancelCoachingAndTherapyRequest({
  eventRequestID,
  open,
  onClose,
  onCancelEventSuccess,
}: Props) {
  const { t } = useTranslation<Namespace<'book' | 'shared'>>([
    'book',
    'shared',
  ]);

  const [cancellationReason, setCancellationReason] =
    useState<keyof RequestCancellationReasonType>();
  const [customReasonText, setCustomReasonText] = useState<string>();

  useEffect(() => {
    if (!open) {
      setCancellationReason(undefined);
      setCustomReasonText(undefined);
    }
  }, [open]);

  const [cancelRequestMutation, { loading: mutationInFlight }] = useMutation<
    cancelEventRequestMutation,
    cancelEventRequestMutationVariables
  >(CANCEL_EVENT_REQUEST_MUTATION, {
    ...BEGatewayQueryContext,
    refetchQueries: [TALK_EVENT_REQUESTS_QUERY],
    onCompleted: data => {
      setCancellationReason(undefined);
      setCustomReasonText(undefined);

      if (data?.cancelEventRequest) {
        onCancelEventSuccess?.();
      }
    },
    onError: () => {
      logException(`Failed to cancel event request ID: ${eventRequestID}`);
      showToast(t('shared:errors.messages.something_wrong'));
    },
  });

  const i18nPath = 'book:sessionDetails.cancel.request.reason';
  const cancellationReasonOptions = [
    { value: 'no_practitioner_response', key: 'noPractitionerResponse' },
    { value: 'session_not_needed', key: 'sessionNotNeeded' },
    { value: 'found_another_practitioner', key: 'foundAnotherPractitioner' },
    { value: 'other', key: 'other' },
  ].map(({ key, value }) => {
    const option: {
      value: keyof RequestCancellationReasonType;
      label: string;
      textArea?: {
        title?: string;
        placeholder?: string;
      };
    } = {
      value: value as keyof RequestCancellationReasonType,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      label: t(`${i18nPath}.${key}` as any),
    };

    if (value === 'other') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      option.label = t(`${i18nPath}.${key}.option` as any);
      option.textArea = {
        title: t(`${i18nPath}.other.label`),
        placeholder: t(`${i18nPath}.other.placeholder`),
      };
    }

    return option;
  });

  const handleSubmit = async () => {
    if (!cancellationReason) {
      return;
    }

    const input: EventRequestCancelInput = {
      id: eventRequestID,
      reason:
        cancellationReason === 'other' && customReasonText
          ? customReasonText
          : cancellationReason,
    };

    await cancelRequestMutation({ variables: { input } });
  };

  const handleClose = () => {
    setCancellationReason(undefined);
    setCustomReasonText(undefined);

    tracking.track('talk-event-request-cancellation-abandoned', {
      eventRequestId: eventRequestID,
    });
    onClose();
  };

  return (
    <Portal>
      <Modal
        open={open}
        onCloseCallback={handleClose}
        closeHeader
        modalContent={() => (
          <Container>
            <MultiChoice
              type={'radio'}
              legend={t('book:sessionDetails.cancel.request.title')}
              options={cancellationReasonOptions}
              onSelected={selected =>
                setCancellationReason(
                  selected as keyof RequestCancellationReasonType,
                )
              }
              onTextChange={(choice: string, text: string) => {
                if (choice === 'other') {
                  setCustomReasonText(text);
                }
              }}
            />

            <SubmitButton
              label={t('book:sessionDetails.cancel.request.cta')}
              loading={mutationInFlight}
              disabled={!cancellationReason || mutationInFlight}
              onClick={handleSubmit}
            />
          </Container>
        )}
      />
    </Portal>
  );
}
